import { useEffect } from "react";
import { useApi } from "../../api/context";
import { useEntitlement } from "../billing/useEntitlement";
import { getSupportToken } from "./support-session";

/** Server presence TTL is 90 s (auto-assignment) and auto-away is 5 min; ping well inside both. */
export const HEARTBEAT_INTERVAL_MS = 45_000;

/**
 * OWN-133: tells the server this agent is online (`AgentPresence`) while the dashboard is open and
 * visible. Auto-assignment only picks agents with a fresh heartbeat; a hidden tab stops pinging,
 * so the agent falls to away/offline on its own. Failures are ignored (best effort).
 */
export function PresenceHeartbeat({ workspaceId }: { workspaceId: string }) {
  const api = useApi();
  // Plans without `routing.sla` answer 403: never ping until the entitlement is known to be on.
  const allowed = useEntitlement(workspaceId, "routing.sla")?.enabled === true;
  useEffect(() => {
    if (!allowed) return;
    // Support sessions are read-only visits by staff: they must never appear as an online agent.
    if (getSupportToken()) return;
    // A plan without routing keeps answering 403 ENTITLEMENT_*: stop pinging instead of filling the console.
    let stopped = false;
    const ping = () => {
      if (stopped) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      void Promise.resolve(api.agentHeartbeat(workspaceId)).catch((err: unknown) => {
        const e = err as { status?: number; code?: string } | null;
        if (e?.status === 403 && String(e.code ?? "").startsWith("ENTITLEMENT")) stopped = true;
      });
    };
    ping();
    const timer = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    const onVisible = () => ping();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, workspaceId, allowed]);
  return null;
}
